import React from 'react';

import { I18n } from "react-redux-i18n";
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@common/components/modal';

import { getTestAttributes } from "@common/utils/tests";
import { Button } from "@common/components/Button";
import { Flex } from "@common/components/Flex";
import { Questionnaires } from "@common/components/questionnaires";
import { HttpErrorDto } from '@common/dto/http';
import { QuestionnaireListDto } from '@common/dto/questionnaire/list-dto';
import {EmailAddress} from "@common/components/new-invite/components/email-address";
import {saveIndividualEmail} from "@common/actions/individuals";
import { isEmail } from '@common/utils/email';

import "./new-questionnaire.styles.scss";

export type NewQuestionnaireProps = {
  moduleName: string;
  title: string;
  onClose: () => void;
  showErrorMessage: (msg: string) => void;
  onSubmit: (questionnaire: QuestionnaireListDto | undefined) => Promise<void>;
  getQuestionnaires: () => Promise<Array<QuestionnaireListDto>>;
  hasEmail?: boolean | undefined;
  newQuestionnaire?: QuestionnaireListDto | undefined;
  individualId?: string | undefined;
};

export const NewQuestionnaire: React.FC<NewQuestionnaireProps> = (props) => {
  const { onClose, onSubmit, title, showErrorMessage, moduleName, getQuestionnaires, hasEmail, newQuestionnaire, individualId } = props;
  const localModuleName = `${moduleName}-new-questionnaire`;
  const [submitting, setSubmitting] = React.useState(false);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = React.useState<QuestionnaireListDto | undefined>();
  const [emailValidation, setEmailValidation] = React.useState<boolean | undefined>(true);
  const [emailAddress, setEmailAddress] = React.useState<string>('');

  React.useEffect(() => {
    setEmailValidation(hasEmail);
  }, [hasEmail])

  const handleSubmit = async (bypassEmailScreen: boolean) => {
    if (!hasEmail && !bypassEmailScreen) {
      setEmailAddress('');
      setEmailValidation(false);
      return;
    }

    if (!selectedQuestionnaire && !bypassEmailScreen) {
      return;
    }

    setSubmitting(true);

    try {
      const questionnaireToSubmit = selectedQuestionnaire ?? newQuestionnaire;
      if (questionnaireToSubmit) {
        await onSubmit(questionnaireToSubmit);
      } else {
        throw new Error("No questionnaire selected to submit.");
      }
    } catch (err) {
      console.error(err);
      setSubmitting(false);
      showErrorMessage((err as HttpErrorDto | undefined)?.entity || I18n.t("Operation failed"));
    }
  };

  const handleSaveEmail = async() => {
    await saveIndividualEmail({fhq4: newQuestionnaire!.fhq4, globalId: individualId!, questionnaireId: newQuestionnaire!.questionnaireId, email: emailAddress, inviteId: "0"});
    setEmailValidation(true);
    await handleSubmit(true);
  }

  const handlePreviousClick = () => {
    setEmailValidation(true);
    setSubmitting(false);
    setSelectedQuestionnaire(undefined);
  }

  const handleClose = () => {
    setEmailValidation(false);
    setSubmitting(false);
    setSelectedQuestionnaire(undefined);
    setEmailAddress('');
    onClose();
  };


  return (
    <Modal
      show
      animation={false}
      bsSize="medium"
      backdrop="static"
      bsClass="bootstrap modal"
      className="NewQuestionnaire"
      onEscapeKeyDown={handleClose}
      onHide={handleClose}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle
          {...getTestAttributes(localModuleName, "header", "title")}
        >
          {title}
        </ModalTitle>
      </ModalHeader>

      <ModalBody>
        {emailValidation && (
          <Questionnaires
            key={'Questionnaire' + emailValidation}
            getData={getQuestionnaires}
            title={I18n.t("Select a Questionnaire")}
            selectedQuestionnaire={selectedQuestionnaire}
            onSelectedQuestionnaireChange={setSelectedQuestionnaire}
          />
        )}

        {!emailValidation && (
          <EmailAddress
            key={'Email' + emailValidation}
            defaultValue={emailAddress}
            onChange={setEmailAddress}
          />
        )}

      </ModalBody>
      <ModalFooter>
        <Button
          disabled={submitting}
          {...getTestAttributes(moduleName, "cancel", "button")}
          onClick={onClose}
        >
          {I18n.t("Cancel")}
        </Button>

          {!hasEmail && !emailValidation && (
            <Flex>
              <Button onClick={handlePreviousClick}>
                {I18n.t('Previous')}
              </Button>
              <Button bsStyle="primary" disabled={!emailAddress || !isEmail(emailAddress)} onClick={handleSaveEmail}>
                {I18n.t('Next')}
              </Button>
            </Flex>
          )}
        {(hasEmail || emailValidation) && (
          <Button
            disabled={submitting || selectedQuestionnaire === undefined}
            bsStyle="primary"
            {...getTestAttributes(localModuleName, "submit", "button")}
            onClick={() => {handleSubmit(false)}}
          >
            {submitting ? I18n.t("Loading") : I18n.t('Launch Questionnaire')}
          </Button>
        )}

      </ModalFooter>
    </Modal>
  );
};
